import { queryOptions } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_CHAT_FEEDBACKS,
  ADMIN_CHAT_SESSION_DETAIL,
} from "@/lib/graphql/queries/chat-feedbacks";
import type {
  ChatFeedbackItem,
  ChatFeedbacksFilters,
  ChatSessionDetail,
} from "@/types/chat-feedback";
import type { PaginationInput, PaginationMeta } from "@/types/common";

export const chatFeedbackKeys = {
  all: ["chatFeedbacks"] as const,
  lists: () => [...chatFeedbackKeys.all, "list"] as const,
  list: (filters: ChatFeedbacksFilters & PaginationInput) =>
    [...chatFeedbackKeys.lists(), filters] as const,
  sessions: () => [...chatFeedbackKeys.all, "session"] as const,
  session: (sessionId: string, feedbackMessageId: string) =>
    [...chatFeedbackKeys.sessions(), sessionId, feedbackMessageId] as const,
};

interface AdminChatFeedbacksResponse {
  items: ChatFeedbackItem[];
  meta: PaginationMeta;
}

export function chatFeedbacksQueryOptions(
  filters: ChatFeedbacksFilters,
  pagination: PaginationInput
) {
  return queryOptions({
    queryKey: chatFeedbackKeys.list({ ...filters, ...pagination }),
    queryFn: async () => {
      // GraphQL enum `AdminChatFeedbackRatingFilter`는 "UP" / "DOWN" 키로 직렬화.
      // 프론트 내부 표현은 소문자 ("up" | "down") → 경계에서 upper-case로 변환.
      const input = {
        page: pagination.page,
        limit: pagination.limit,
        rating: filters.rating
          ? (filters.rating.toUpperCase() as "UP" | "DOWN")
          : undefined,
      };

      const data = await authenticatedRequest<{
        adminChatFeedbacks: AdminChatFeedbacksResponse;
      }>(ADMIN_CHAT_FEEDBACKS, { input });

      return data.adminChatFeedbacks;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function chatSessionDetailQueryOptions(
  sessionId: string | null,
  feedbackMessageId: string | null
) {
  return queryOptions({
    queryKey: chatFeedbackKeys.session(sessionId ?? "", feedbackMessageId ?? ""),
    queryFn: async () => {
      if (!sessionId || !feedbackMessageId) {
        return null as ChatSessionDetail | null;
      }
      const data = await authenticatedRequest<{
        adminChatSessionDetail: ChatSessionDetail;
      }>(ADMIN_CHAT_SESSION_DETAIL, {
        input: { sessionId, feedbackMessageId },
      });
      return data.adminChatSessionDetail;
    },
    enabled: Boolean(sessionId && feedbackMessageId),
    staleTime: 60000,
  });
}
